"use client";

// ============================================================
// CLUBHUB – Dokumente: was in der Adresse steht
// ============================================================
// Alles Steuernde der Ablage steht in der ADRESSE, wie auf jeder
// Liste im Haus: Ordner, Suche, Art, Sortierung, Raster oder Liste
// und die offene Datei. So laesst sich „das Protokoll vom März“ als
// Verweis verschicken, und der Zurueck-Knopf des Telefons geht einen
// Ordner zurueck statt aus der Ablage hinaus.
//
// Anders als `useAnsicht` aus dem Listen-Baukasten schreibt dieser
// Haken mit `history.pushState`/`replaceState` und NICHT ueber den
// Router: gefiltert wird im Browser, ein Ordnerwechsel braucht keine
// Runde zum Server. Next haelt `useSearchParams` dabei synchron.
// Ordner und Datei kommen in den Verlauf (zurueck = vorheriger
// Ordner, Blatt zu), Suche und Filter ersetzen den Eintrag.
// ============================================================

import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ART_REIHE, istSortierung, type DateiArt, type Sortierung } from "@/lib/dokumente/format";

export type Darstellung = "raster" | "liste";

export type Ansicht = {
  ordner: string;
  q: string;
  art: DateiArt | "";
  sort: Sortierung;
  /** Leer = automatisch: Raster, wo Bilder ueberwiegen. */
  darstellung: Darstellung | "";
  datei: string;
  /** Suche nur im offenen Ordner und darunter. */
  nurHier: boolean;
  /** Vorlagen: Suche und Art. */
  vq: string;
  vart: string;
};

type Schluessel = "ordner" | "q" | "art" | "sort" | "ansicht" | "datei" | "in" | "vq" | "vart";

export const ABLAGE_PFAD = "/dokumente";

/**
 * Stammt der aktuelle Verlaufseintrag vom Oeffnen einer Datei? Dann
 * schliesst das Blatt mit `history.back()` — sonst stuende danach
 * ein Eintrag „Blatt offen“ im Verlauf, und der naechste Zurueck-
 * Knopf taete scheinbar nichts. Jede Navigation im Verlauf setzt
 * das Merkmal zurueck.
 */
let eintragVomOeffnen = false;
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    eintragVomOeffnen = false;
  });
}

export function useAdresse() {
  const params = useSearchParams();
  const pfad = usePathname();
  const router = useRouter();

  const art = params.get("art") ?? "";
  const sort = params.get("sort");
  const darstellung = params.get("ansicht");

  const ansicht: Ansicht = {
    ordner: params.get("ordner") ?? "",
    q: params.get("q") ?? "",
    art: (ART_REIHE as string[]).includes(art) ? (art as DateiArt) : "",
    sort: istSortierung(sort) ? sort : "name",
    darstellung: darstellung === "raster" || darstellung === "liste" ? darstellung : "",
    datei: params.get("datei") ?? "",
    nurHier: params.get("in") === "hier",
    vq: params.get("vq") ?? "",
    vart: params.get("vart") ?? "",
  };

  /**
   * Setzt Teile der Adresse. `verlauf` legt einen Eintrag an (Ordner,
   * Datei), sonst wird ersetzt (Suche, Filter). Gelesen wird die
   * Adresse im AUGENBLICK des Aufrufs, nicht beim Zeichnen: ein
   * Toast-Knopf („Zum Ordner“) kann aufgerufen werden, wenn man
   * laengst auf einer anderen Seite ist. Von dort (Vorlagen) geht es
   * ueber den Router nach `/dokumente` — nur dort zeichnet sich der
   * Ordner.
   */
  function setzen(aenderungen: Partial<Record<Schluessel, string | null>>, verlauf = false) {
    const fremd = window.location.pathname !== ABLAGE_PFAD;
    const naechste = new URLSearchParams(fremd ? "" : window.location.search);
    for (const [name, wert] of Object.entries(aenderungen)) {
      if (wert === null || wert === undefined || wert === "") naechste.delete(name);
      else naechste.set(name, wert);
    }
    const frage = naechste.toString();
    const ziel = frage ? `${ABLAGE_PFAD}?${frage}` : ABLAGE_PFAD;
    if (fremd) {
      router.push(ziel);
      return;
    }
    if (ziel === `${window.location.pathname}${window.location.search}`) return;
    if (verlauf) {
      window.history.pushState(null, "", ziel);
      eintragVomOeffnen = Boolean(aenderungen.datei);
    } else {
      window.history.replaceState(null, "", ziel);
      if ("datei" in aenderungen && !aenderungen.datei) eintragVomOeffnen = false;
    }
  }

  /** Das Blatt schliessen: zurueck, wenn das Oeffnen einen Eintrag angelegt hat. */
  function dateiSchliessen() {
    if (eintragVomOeffnen && window.location.pathname === ABLAGE_PFAD) {
      eintragVomOeffnen = false;
      window.history.back();
      return;
    }
    setzen({ datei: null });
  }

  /** Nur fuer die Vorlagen-Seite: Suche und Art dort. */
  function setzenHier(aenderungen: Partial<Record<Schluessel, string | null>>) {
    const naechste = new URLSearchParams(window.location.search);
    for (const [name, wert] of Object.entries(aenderungen)) {
      if (!wert) naechste.delete(name);
      else naechste.set(name, wert);
    }
    const frage = naechste.toString();
    window.history.replaceState(null, "", frage ? `${window.location.pathname}?${frage}` : window.location.pathname);
  }

  return { ansicht, setzen, setzenHier, dateiSchliessen, pfad };
}

/**
 * Ein Feld, das in die Adresse SPIEGELT, aber nicht an ihr haengt.
 *
 * Next uebernimmt `replaceState` erst in einem Uebergang. Haengt der
 * Wert eines Eingabefelds direkt an der Adresse, hinkt er hinterher:
 * der Cursor springt ans Ende, schnell Getipptes geht verloren. Der
 * Text steht deshalb hier im Zustand; was in der Adresse ankommt und
 * nur das Echo eigener Eingaben ist, wird verworfen. Was von woanders
 * kommt (Zurueck-Knopf, ein Verweis), wird uebernommen.
 */
export function useSpiegel(adresswert: string) {
  const [text, setText] = useState(adresswert);
  const [unterwegs, setUnterwegs] = useState<string[]>([]);
  const [gesehen, setGesehen] = useState(adresswert);

  if (adresswert !== gesehen) {
    setGesehen(adresswert);
    const i = unterwegs.indexOf(adresswert);
    if (i >= 0) {
      setUnterwegs(unterwegs.slice(i + 1));
    } else {
      setUnterwegs([]);
      setText(adresswert);
    }
  }

  function merken(wert: string) {
    setText(wert);
    setUnterwegs((u) => (wert === (u.at(-1) ?? gesehen) ? u : [...u, wert]));
  }

  return [text, merken] as const;
}
